import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, User, Phone, Calendar, Shield, Activity,
  AlertTriangle, TrendingUp, TrendingDown, Minus, FileText,
  ClipboardList, Beaker, Clock, MapPin, ChevronDown, ChevronUp,
  ArrowUpRight, ArrowDownRight, Download, Heart,
} from 'lucide-react';
import { resultsAnalysisAPI } from '../../services/api';
import toast from 'react-hot-toast';

const FLAG = {
  H: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'مرتفع', icon: ArrowUpRight, color: '#EF4444' },
  L: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'منخفض', icon: ArrowDownRight, color: '#3B82F6' },
  N: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'طبيعي', icon: Minus, color: '#10B981' },
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA-u-ca-gregory', { year: 'numeric', month: 'long', day: 'numeric' }) : '-';
const formatShortDate = (d) => d ? new Date(d).toLocaleDateString('ar-SA-u-ca-gregory', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '-';
const calcAge = (dob) => dob ? Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : '-';

const StatusBadge = ({ status }) => {
  const map = {
    new: { l: 'جديد', c: 'bg-blue-50 text-blue-700 border-blue-200' },
    sample_collected: { l: 'تم جمع العينة', c: 'bg-amber-50 text-amber-700 border-amber-200' },
    in_progress: { l: 'قيد التنفيذ', c: 'bg-purple-50 text-purple-700 border-purple-200' },
    completed: { l: 'مكتمل', c: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    cancelled: { l: 'ملغي', c: 'bg-red-50 text-red-700 border-red-200' },
    released: { l: 'تم الإصدار', c: 'bg-teal-50 text-teal-700 border-teal-200' },
    online_unconfirmed: { l: 'ويب غير مؤكد', c: 'bg-orange-50 text-orange-700 border-orange-200' },
  };
  const s = map[status] || { l: status || '-', c: 'bg-slate-50 text-slate-600 border-slate-200' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${s.c}`}>{s.l}</span>;
};

const PatientProfileAnalysis = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedVisit, setExpandedVisit] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!customerId || customerId === 'undefined' || customerId === 'null') {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await resultsAnalysisAPI.getPatientProfile(customerId);
        setData(res.data?.data || null);
        // Auto-expand first visit
        if (res.data?.data?.visits?.[0]) setExpandedVisit(String(res.data.data.visits[0]._id));
      } catch (e) {
        toast.error('فشل تحميل بيانات المريض');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customerId]);

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center" dir="rtl">
        <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-10 text-center text-slate-500" dir="rtl">
        <p>لم يتم العثور على بيانات المريض</p>
        <button onClick={() => navigate('/lab/analysis')} className="mt-3 text-blue-600 text-sm hover:underline">العودة لتحليل النتائج</button>
      </div>
    );
  }

  const { patient, summary, visits, abnormal_history } = data;

  return (
    <div className="p-3 sm:p-4 lg:p-6" dir="rtl">
      {/* Back */}
      <button onClick={() => navigate('/lab/analysis')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 mb-4 transition-colors">
        <ArrowRight className="w-4 h-4" />
        العودة لتحليل النتائج
      </button>

      {/* ══════ Patient Header Card ══════ */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6">
        <div className="bg-gradient-to-l from-blue-600 to-blue-700 px-5 sm:px-8 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
              <span className="text-2xl font-bold text-white">{patient.full_name?.charAt(0) || '؟'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white">{patient.full_name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-blue-100 text-sm">
                <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> {patient.national_id || '-'}</span>
                <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {patient.phone || '-'}</span>
                <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {calcAge(patient.date_of_birth)} سنة</span>
                <span className="px-2 py-0.5 rounded-md bg-white/20 text-white text-xs font-medium">{patient.gender === 'Male' ? 'ذكر' : 'أنثى'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-100 border-t border-slate-100">
          {[
            { icon: ClipboardList, label: 'إجمالي الزيارات', value: summary.total_visits, color: 'blue' },
            { icon: Beaker, label: 'إجمالي التحاليل', value: summary.total_tests, color: 'purple' },
            { icon: AlertTriangle, label: 'نتائج غير طبيعية', value: summary.total_abnormal, color: 'red' },
            { icon: Clock, label: 'آخر زيارة', value: formatShortDate(summary.last_visit), color: 'emerald' },
          ].map((s, i) => (
            <div key={i} className="p-4 sm:p-5 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-${s.color}-50 flex items-center justify-center shrink-0`}>
                <s.icon className={`w-5 h-5 text-${s.color}-600`} />
              </div>
              <div>
                <p className="text-[11px] text-slate-500">{s.label}</p>
                <p className="text-lg font-bold text-slate-900">{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ══════ Visits Timeline (Left / Main) ══════ */}
        <div className="xl:col-span-2 space-y-4">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            سجل الزيارات والنتائج
            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{visits.length}</span>
          </h2>

          {visits.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-400">
              لا توجد زيارات مسجلة
            </div>
          )}

          {visits.map((visit) => {
            const isOpen = expandedVisit === String(visit._id);
            const abnormalInVisit = visit.tests.reduce((sum, t) => {
              return sum + (t.result?.components || []).filter((c) => c.abnormal_flag === 'H' || c.abnormal_flag === 'L').length;
            }, 0);
            return (
              <motion.div key={visit._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {/* Visit header */}
                <button onClick={() => setExpandedVisit(isOpen ? null : String(visit._id))}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50/50 transition-colors text-right">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{formatDate(visit.booking_date)}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-500">{visit.booking_no}</span>
                        <span className="text-[10px] text-slate-400">•</span>
                        <span className="text-[10px] text-slate-500">{visit.branch}</span>
                        {visit.visit_type === 'home_visit' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-100">زيارة منزلية</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatusBadge status={visit.status} />
                    <span className="text-xs text-slate-500">{visit.tests.length} تحليل</span>
                    {abnormalInVisit > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 font-bold border border-red-100">{abnormalInVisit} غير طبيعي</span>
                    )}
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {/* Expanded tests */}
                {isOpen && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {visit.tests.map((test) => {
                      const components = test.result?.components || [];
                      const hasResults = components.length > 0;
                      return (
                        <div key={test._id} className="px-5 py-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Beaker className="w-4 h-4 text-slate-400" />
                            <span className="text-sm font-semibold text-slate-800">{test.test_name}</span>
                            <StatusBadge status={test.result?.status || test.status} />
                          </div>

                          {!hasResults ? (
                            <p className="text-xs text-slate-400 mr-6">لم يتم إدخال النتيجة بعد</p>
                          ) : (
                            <div className="mr-6 bg-slate-50/50 rounded-xl overflow-hidden border border-slate-100">
                              <table className="w-full">
                                <thead>
                                  <tr className="text-[10px] text-slate-500 uppercase tracking-wider">
                                    <th className="px-3 py-2 text-right font-semibold">العنصر</th>
                                    <th className="px-3 py-2 text-center font-semibold">النتيجة</th>
                                    <th className="px-3 py-2 text-center font-semibold">الوحدة</th>
                                    <th className="px-3 py-2 text-center font-semibold">الحالة</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  {components.map((comp, ci) => {
                                    const f = FLAG[comp.abnormal_flag] || null;
                                    return (
                                      <tr key={ci} className="hover:bg-white/80 transition-colors">
                                        <td className="px-3 py-2 text-sm text-slate-700">{comp.component_name || '-'}</td>
                                        <td className="px-3 py-2 text-center">
                                          <span className={`text-sm font-bold ${comp.abnormal_flag === 'H' ? 'text-red-600' : comp.abnormal_flag === 'L' ? 'text-blue-600' : 'text-slate-900'}`}>
                                            {comp.value_numeric ?? comp.value_text ?? '-'}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-center text-xs text-slate-500">{comp.unit || '-'}</td>
                                        <td className="px-3 py-2 text-center">
                                          {f ? (
                                            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${f.bg} ${f.text} ${f.border}`}>
                                              <f.icon className="w-3 h-3" />{f.label}
                                            </span>
                                          ) : <span className="text-[10px] text-slate-400">-</span>}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {test.result?.comment && (
                            <p className="text-xs text-slate-500 mr-6 mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                              <strong>ملاحظات:</strong> {test.result.comment}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* ══════ Sidebar - Abnormal History ══════ */}
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            سجل النتائج غير الطبيعية
            <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-bold">{abnormal_history.length}</span>
          </h2>

          {abnormal_history.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Heart className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-sm text-slate-500">لا توجد نتائج غير طبيعية — ممتاز!</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-50">
                {abnormal_history.map((h, i) => {
                  const f = FLAG[h.flag] || FLAG.N;
                  return (
                    <div key={i} className="px-4 py-3 hover:bg-slate-50/50 transition-colors">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-bold text-slate-800 truncate">{h.test}</span>
                        <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${f.bg} ${f.text} ${f.border}`}>
                          <f.icon className="w-2.5 h-2.5" />{f.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-500">{h.component}</span>
                        <span className="font-bold text-slate-700">{h.value ?? '-'} <span className="text-slate-400 font-normal">{h.unit || ''}</span></span>
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">{formatShortDate(h.date)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientProfileAnalysis;
